import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Copy, Download, Lock, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function WeeklyReportCard({ projectId }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const isGold = user?.plan === "gold";
  const langMismatch = report && report._lang && report._lang !== lang;

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/weekly-report?lang=${lang}`);
      setReport({ ...data, _lang: lang });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!report) return;
    const plain = plainTextFromSections(report.sections, lang);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(plain);
      } else {
        const ta = document.createElement("textarea");
        ta.value = plain;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(t("report_copied"));
    } catch {
      toast.error("Clipboard error");
    }
  };

  const downloadPdf = () => {
    if (!report) return;
    const token = localStorage.getItem("obra_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/weekly-report/${report.id}.pdf?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank", "noopener");
  };

  if (!isGold) {
    return (
      <div className="surface-card p-6 relative overflow-hidden" data-testid="weekly-report-card">
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#F5F1E6] opacity-70" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#B8933A]" />
            <p className="label-eyebrow text-[#B8933A]">{t("weekly_report")}</p>
          </div>
          <h3 className="font-[Fraunces] text-2xl font-bold text-neutral-950 mt-2">
            {t("weekly_report_gold_only")}
          </h3>
          <p className="text-sm text-neutral-600 mt-2 max-w-lg">
            {t("weekly_report_gold_desc")}
          </p>
          <Link to="/app/billing">
            <Button className="mt-4 bg-[#B8933A] hover:bg-[#8C6D22]" data-testid="upgrade-to-gold-button">
              <Sparkles className="w-4 h-4 mr-1.5" />{t("upgrade_to_gold")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-card p-6 space-y-5" data-testid="weekly-report-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#B8933A]" />
            <p className="label-eyebrow">{t("weekly_report")}</p>
          </div>
          <h3 className="font-[Fraunces] text-xl font-bold text-neutral-950 mt-1">
            {report ? report.sections?.executive_summary?.split(".")[0] + "." : t("weekly_report")}
          </h3>
          <p className="text-xs text-neutral-500 mt-1">
            {report ? `${t("report_generated_at")}: ${report.generated_at}` : t("weekly_report_sub")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {report && (
            <>
              <Button variant="outline" size="sm" onClick={copy} data-testid="copy-report-button">
                <Copy className="w-3.5 h-3.5 mr-1" />{t("copy_report")}
              </Button>
              <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-report-pdf-button">
                <Download className="w-3.5 h-3.5 mr-1" />{t("download_report_pdf")}
              </Button>
            </>
          )}
          <Button
            onClick={generate}
            disabled={loading}
            className="bg-[#B8933A] hover:bg-[#8C6D22]"
            size="sm"
            data-testid="generate-report-button"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> :
              report ? <RefreshCw className="w-4 h-4 mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            {loading ? t("generating") : (report ? t("regenerate_report") : t("generate_report"))}
          </Button>
        </div>
      </div>

      {loading && !report && (
        <div className="border border-dashed border-neutral-300 rounded-md p-8 text-center" data-testid="weekly-report-loading">
          <Loader2 className="w-6 h-6 text-[#B8933A] mx-auto animate-spin" />
          <p className="font-[Fraunces] text-lg mt-3">{t("generating_report")}</p>
          <p className="text-xs text-neutral-500 mt-1">{t("generating_report_sub")}</p>
        </div>
      )}

      {!loading && !report && (
        <div className="border border-dashed border-neutral-300 rounded-md p-8 text-center">
          <p className="text-sm text-neutral-500">{t("no_report_yet")}</p>
        </div>
      )}

      {report && (
        <div className="border border-neutral-200 rounded-md bg-white p-6" data-testid="weekly-report-preview">
          {langMismatch && (
            <div className="mb-4 -mt-1 rounded-md border border-[#B8933A]/30 bg-[#FAF6EB] px-3 py-2 text-xs text-[#8C6D22]">
              {lang === "es"
                ? "Este informe está en otro idioma. Pulsa Regenerar para redactarlo en español."
                : "This report is in another language. Click Regenerate to redraft it in English."}
            </div>
          )}
          <ReportPreview sections={report.sections} />
        </div>
      )}
    </div>
  );
}

function ReportPreview({ sections }) {
  const { t } = useLang();
  const lists = [
    ["progress_this_week", sections?.progress_this_week],
    ["upcoming_next_week", sections?.upcoming_next_week],
    ["open_issues", sections?.open_issues],
    ["risks_and_blockers", sections?.risks_and_blockers],
  ];
  return (
    <div className="space-y-6">
      <div>
        <p className="label-eyebrow text-[#B8933A]">{t("executive_summary")}</p>
        <p className="text-sm text-neutral-800 leading-relaxed mt-2">
          {sections?.executive_summary || "—"}
        </p>
      </div>
      {lists.map(([key, items]) => (
        <div key={key}>
          <p className="label-eyebrow text-[#B8933A]">{t(key)}</p>
          {(items || []).length === 0 ? (
            <p className="text-sm text-neutral-500 mt-2">—</p>
          ) : (
            <ul className="mt-2 space-y-1.5 text-sm text-neutral-800 leading-relaxed">
              {items.map((x, i) => (
                <li key={i} className="pl-4 relative">
                  <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#B8933A]" />
                  {x}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
      <div>
        <p className="label-eyebrow text-[#B8933A]">{t("closing_note")}</p>
        <p className="text-sm text-neutral-800 leading-relaxed mt-2 italic">
          {sections?.closing_note || "—"}
        </p>
      </div>
    </div>
  );
}

function plainTextFromSections(s, lang) {
  const L = (en, es) => (lang === "es" ? es : en);
  const lines = [];
  lines.push(L("WEEKLY CLIENT REPORT", "INFORME SEMANAL AL CLIENTE"));
  lines.push("");
  lines.push(L("Executive summary", "Resumen ejecutivo"));
  lines.push(s?.executive_summary || "—");
  const sections = [
    [L("Progress this week", "Avances de la semana"), s?.progress_this_week],
    [L("Upcoming next week", "Próxima semana"), s?.upcoming_next_week],
    [L("Open issues", "Incidencias abiertas"), s?.open_issues],
    [L("Risks & blockers", "Riesgos y bloqueos"), s?.risks_and_blockers],
  ];
  for (const [title, items] of sections) {
    lines.push("");
    lines.push(title);
    for (const x of items || []) lines.push(`• ${x}`);
    if (!(items || []).length) lines.push("—");
  }
  lines.push("");
  lines.push(L("Closing note", "Cierre"));
  lines.push(s?.closing_note || "—");
  return lines.join("\n");
}
